import * as moment from "moment";
import lodash from "lodash";
import { SET_ACTIVE_QUEUE } from "./mutation-types";
import {
    MAX_SRS,
    SRS_BASE_INTERVAL,
    VERB_QUESTION_TYPES
} from "@/constants.js";

const sortSRSTrackersByLastChange = function compare(a, b) {
    if (a.lastChange < b.lastChange) {
        return -1;
    } else if (a.lastChange > b.lastChange) {
        return 1;
    } else return 0;
};

const wordMaxedOut = function(word) {
    return word.srs >= MAX_SRS;
};
const wordAvailable = function(word) {
    return Date.now() > wordAvailableTime(word);
};
const wordActive = function(word) {
    return !wordMaxedOut(word) && wordAvailable(word);
};
const wordAvailableTime = function(word) {
    return word.lastChange + word.srs * SRS_BASE_INTERVAL;
};
const waitingForWord = function(word) {
    return Date.now() < wordAvailableTime(word);
};
const getters = {
    queueMaxed: state => {
        return state.queue.every(word => wordMaxedOut(word));
    },
    wordSRS: (state, getters) => word => {
        for (var i = 0; i < state.queue.length; i++) {
            if (state.queue[i].id == word.id) {
                return state.queue[i].srs;
            }
        }
        return 0;
    },
    willLevelUp: (state, getters) => {
        return !getters.lessonAvailable && getters.queueMaxed;
    },
    lessonAvailable: (state, getters) => {
        return (!state.user.lessonReviewed ||
            (getters.unstudiedVocab && getters.unstudiedVocab.length > 0)
        );
    },
    currentLesson: state => {
        if (state.user && state.lessons)
            return state.lessons[state.user.currentLesson];
    },
    reviews: (state, getters) => {
        if (getters.activeItems) return getters.activeItems.length;
    },
    reviewsAvailable: (state, getters) => {
        if (getters.activeItems) return !!getters.activeItems.length;
    },
    nextReviewTime: (state, getters) => {
        if (getters.activeItems.length > 0) return "is available now";
        else {
            var nextReview = state.queue
                .filter(wt => !wordMaxedOut(wt))
                .sort(sortSRSTrackersByLastChange)[0];
            if (nextReview) return moment(wordAvailableTime(nextReview)).fromNow();
        }
    },
    loading: state => {
        return state.loading;
    },
    error: state => {
        return state.error;
    },
    activeItems: state => {
        var readyToReview = state.queue.filter(word => wordActive(word));
        var result = [];
        for (var i = 0; i < readyToReview.length; i++) {
            var question;
            var type = readyToReview[i].type;
            if (type == "vocab") {
                question = state.vocabulary.find(w => w.id === readyToReview[i].id);
            }
            if (type == "fm") {
                question = state.fmquestions.find(w => w.id === readyToReview[i].id);
            }
            if (VERB_QUESTION_TYPES.indexOf(type) >= 0) {
                question = state.verbQuestions.find(w => w.id === readyToReview[i].id);
            }
            if (!question) {
                console.error("could not retrieve question from question lists: ");
                console.error(readyToReview[i]);
            }
            result.push(question);
        }
        return result;
    },
    availableItems: state => {
        var readyToReview = state.queue.filter(word => wordAvailable(word));
        var result = [];
        for (var i = 0; i < readyToReview.length; i++) {
            var question;
            var type = readyToReview[i].type;
            if (type == "vocab") {
                question = state.vocabulary.find(w => w.id === readyToReview[i].id);
            }
            if (type == "fm") {
                question = state.fmquestions.find(w => w.id === readyToReview[i].id);
            }
            if (type == "paleoquiz"){
                question = state.paleoQuestions.find(w => w.id === readyToReview[i].id);
            }
            if (VERB_QUESTION_TYPES.indexOf(type) >= 0) {
                question = state.verbQuestions.find(w => w.id === readyToReview[i].id);
            }
            result.push(question);
        }
        return result;
    },
    joinDate: state => {
        if (state.user) return moment(state.user.joinDate).format("MMM Do YYYY");
    },
    getVocabByLesson: state => lessonNumber => {
        if (state.lessons && state.lessons.length > 0) {
            var lesson = state.lessons[lessonNumber];
            if (state.vocabulary && lesson)
                return state.vocabulary.filter(word => word.lesson === lesson.id);
        }
    },
    getCurrentLessonVocab: (state, getters) => {
        if (state.user) return getters.getVocabByLesson(state.user.currentLesson);
    },
    getVocabByID: state => wordID => {
        return state.vocabulary.find(word => word.id === wordID);
    },
    isWordInQueue: state => word => {
        for (var i = 0; i < state.queue.length; i++) {
            if (state.queue[i].id == word.id) return true;
        }
        return false;
    },
    wordAvailableTime: state => word => {
        return word.lastChange + word.srs * SRS_BASE_INTERVAL;
    },
    unstudiedVocab: (state, getters) => {
        //move to dashboard computed prop
        if (getters.getCurrentLessonVocab) {
            return getters.getCurrentLessonVocab.filter(
                word => !getters.isWordInQueue(word)
            );
        } else return [];
    },
    activeWords: (state, getters) => {
        state.queue.filter(word => wordActive(word));
    },
    image: (state) => ID => {
        return state.images.find(i => i.id === ID)
    },
    progress: (state, getters) => {
        //This works for lesson 1, but then always says 100%. Need to account for current lesson.
        var progress = 0;
        var current = state.queue;
        var num_words = state.queue.length;
        var i;
        for (i = 0; i < num_words; i++) {
            var word = current[i];
            if (wordMaxedOut(word)) {
                progress = progress + 1;
            }
        }
        return progress * (100 / num_words);
    },
    progressInfo: (state, getters) => {
        var words = [];
        var current = state.queue;
        var num_words = state.queue.length;
        var i;
        for (i = 0; i < num_words; i++) {
            var questionTracker = state.queue[i];
            if (questionTracker.type == "vocab") {
                var curWord = getters.getVocabByID(questionTracker.id).emiWord;
                var curSRS = questionTracker.srs;

                var curValue = 0;
                if (curSRS == 2) curValue = 50;
                if (curSRS == 3) curValue = 100;

                var curPOS = getters.getVocabByID(questionTracker.id).partOfSpeech;
                var part = "";
                if (curPOS == 1) part = "Noun";
                if (curPOS == 2) part = "Adjective";
                if (curPOS == 3) part = "Verb";
                if (curPOS == 4) part = "Adverb";
                if (curPOS == 5) part = "Article";
                if (curPOS == 6) part = "Conjunction";
                if (curPOS == 7) part = "";
                if (curPOS == 8) part = "";

                var word = { word: curWord, type: part, srs: curSRS, value: curValue };
                words.push(word);
            }
        }
        return words;
    }
};

export default getters;